/**
 * Calendar arithmetic on `YYYY-MM-DD` strings (NFR-9). Weeks start on Monday (D27). Pure and
 * timezone-free: "today" is always given by the caller.
 */

function toUtc(date: string): Date {
  return new Date(`${date}T00:00:00Z`);
}

function fromUtc(date: Date): string {
  return date.toISOString().slice(0, 10);
}

export function addDays(date: string, days: number): string {
  const value = toUtc(date);
  value.setUTCDate(value.getUTCDate() + days);
  return fromUtc(value);
}

/** Monday of the week containing `date`. */
export function startOfWeek(date: string): string {
  const day = toUtc(date).getUTCDay(); // 0 = Sunday
  return addDays(date, -((day + 6) % 7));
}

export function endOfWeek(date: string): string {
  return addDays(startOfWeek(date), 6);
}

export function startOfMonth(date: string): string {
  return `${date.slice(0, 7)}-01`;
}

export function endOfMonth(date: string): string {
  const value = toUtc(startOfMonth(date));
  value.setUTCMonth(value.getUTCMonth() + 1);
  value.setUTCDate(0);
  return fromUtc(value);
}

/** Inclusive range of calendar dates. */
export interface DateRange {
  readonly from: string;
  readonly to: string;
}

export function thisWeek(today: string): DateRange {
  return { from: startOfWeek(today), to: endOfWeek(today) };
}

export function nextWeek(today: string): DateRange {
  const from = addDays(startOfWeek(today), 7);
  return { from, to: addDays(from, 6) };
}

export function thisMonth(today: string): DateRange {
  return { from: startOfMonth(today), to: endOfMonth(today) };
}

export function inRange(date: string, range: DateRange): boolean {
  return date >= range.from && date <= range.to;
}
